import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type {
  ClientToServerEvents,
  ServerToClientEvents,
  LobbyUpdate,
} from '@srcbattle/shared';
import { api, ApiRequestError, type LobbyView } from './api.ts';

type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export interface LobbyState {
  lobby: LobbyView | null;
  error: string | null;
  connected: boolean;
  /** Forces a fetch, for the cases a socket event would not cover. */
  refresh: () => Promise<void>;
}

/**
 * Keeps one lobby in sync.
 *
 * The socket pushes a full snapshot on every change rather than a diff: a lobby
 * is small, game nights are short, and a client that misses one delta would
 * otherwise drift silently. The initial fetch covers the window before the
 * socket has subscribed.
 */
export function useLobby(lobbyId: string | null): LobbyState {
  const [lobby, setLobby] = useState<LobbyView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<AppSocket | null>(null);

  const refresh = useCallback(async () => {
    if (!lobbyId) return;
    try {
      setLobby(await api.getLobby(lobbyId));
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not load the lobby.');
    }
  }, [lobbyId]);

  useEffect(() => {
    if (!lobbyId) {
      setLobby(null);
      return;
    }

    void refresh();

    const socket: AppSocket = io({ withCredentials: true });
    socketRef.current = socket;

    const subscribe = () => {
      socket.emit('lobby:subscribe', { lobbyId }, (result) => {
        if (!result.ok) setError('You are not in that lobby.');
      });
    };

    socket.on('connect', () => {
      setConnected(true);
      subscribe();
      // Re-fetch on reconnect: anything that changed while the socket was down
      // was never delivered, and the snapshot in hand is stale.
      void refresh();
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('lobby:update', (update: LobbyUpdate) => {
      if (update.id === lobbyId) setLobby(update as unknown as LobbyView);
    });
    socket.on('lobby:closed', () => setError('That lobby has closed.'));

    return () => {
      socket.emit('lobby:unsubscribe', { lobbyId });
      socket.close();
      socketRef.current = null;
    };
  }, [lobbyId, refresh]);

  return { lobby, error, connected, refresh };
}
